import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Badge, Button, OverlayTrigger, Tooltip, Spinner, Form, Table, Modal, FloatingLabel } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { FiCalendar, FiMapPin, FiClock, FiGlobe, FiArrowLeft, FiEdit, FiPlus, FiTrash2, FiCheckCircle, FiMaximize } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';

const TrainingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [training, setTraining] = useState(null);
    const [allowEdit, setAllowEdit] = useState(true);
    const [savingDoubleCheck, setSavingDoubleCheck] = useState(false);
    
    // Turmas States
    const [turmas, setTurmas] = useState([]);
    const [loadingTurmas, setLoadingTurmas] = useState(false);
    const [showTurmaModal, setShowTurmaModal] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [selectedTurma, setSelectedTurma] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [submittingTurma, setSubmittingTurma] = useState(false);

    const initialTurmaForm = {
        titulo: '',
        data: '',
        time: '',
        local: '',
        sala_id: '',
        duracao: '',
        link_online: '',
        obriga_checkout: false,
        double_check: true,
        tolerance_checkin: 15,
        tolerance_checkout: 15
    };
    const [turmaFormData, setTurmaFormData] = useState(initialTurmaForm);

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const isManagerOrAdmin = user.perfil === 'Administrador' || user.perfil === 'Gestor';

    const fetchTraining = async () => {
        try {
            const response = await api.get(`/trainings/${id}`);
            setTraining(response.data);
        } catch (err) {
            console.error('Erro ao buscar detalhes do treinamento', err);
        }
    };

    const fetchTurmas = async () => {
        setLoadingTurmas(true);
        try {
            const response = await api.get(`/trainings/${id}/turmas`);
            setTurmas(response.data);
        } catch (err) {
            console.error('Erro ao buscar turmas do treinamento', err);
        } finally {
            setLoadingTurmas(false);
        }
    };

    const fetchRooms = async () => {
        try {
            const response = await api.get('/rooms');
            setRooms(response.data.filter(r => r.ativa));
        } catch (err) {
            console.error('Erro ao buscar salas', err);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await api.get('/settings');
            setAllowEdit(response.data.allow_edit_training);
        } catch (err) {
            console.error('Erro ao buscar configurações', err);
        }
    };

    useEffect(() => {
        fetchTraining();
        fetchTurmas();
        fetchSettings();
        if (isManagerOrAdmin) {
            fetchRooms();
        }
    }, [id]);

    if (!training) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    const isConcluded = training.status === 'Concluído';
    const isEditDisabled = !allowEdit || isConcluded;
    const doubleCheckValue = training.double_check !== undefined ? training.double_check : true;

    let tooltipText = "Editar informações do treinamento";
    if (isConcluded) tooltipText = "Treinamento concluído não pode ser editado";
    else if (!allowEdit) tooltipText = "A configuração do sistema não permite editar treinamentos";

    const handleDoubleCheckChange = async (checked) => {
        if (savingDoubleCheck || isEditDisabled) return;

        const previousValue = doubleCheckValue;
        setTraining(prev => ({ ...prev, double_check: checked }));
        setSavingDoubleCheck(true);

        try {
            await api.put(`/trainings/${id}`, { double_check: checked });
        } catch (err) {
            console.error('Erro ao atualizar Double-check', err);
            setTraining(prev => ({ ...prev, double_check: previousValue }));
        } finally {
            setSavingDoubleCheck(false);
        }
    };

    const handleTurmaFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTurmaFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCreateTurmaSubmit = async (e) => {
        e.preventDefault();
        if (!turmaFormData.titulo || !turmaFormData.data || !turmaFormData.time || !turmaFormData.duracao) {
            alert('Por favor, preencha os campos obrigatórios (Título, Data, Hora e Duração).');
            return;
        }

        setSubmittingTurma(true);
        try {
            const dateTime = `${turmaFormData.data}T${turmaFormData.time}:00`;
            const payload = {
                titulo: turmaFormData.titulo,
                data: dateTime,
                local: turmaFormData.local || null,
                sala_id: turmaFormData.sala_id || null,
                duracao: turmaFormData.duracao,
                link_online: turmaFormData.link_online || null,
                obriga_checkout: turmaFormData.obriga_checkout,
                double_check: turmaFormData.double_check,
                tolerance_checkin: parseInt(turmaFormData.tolerance_checkin) || 15,
                tolerance_checkout: parseInt(turmaFormData.tolerance_checkout) || 15
            };

            await api.post(`/trainings/${id}/turmas`, payload);
            setShowTurmaModal(false);
            setTurmaFormData(initialTurmaForm);
            fetchTurmas();
        } catch (err) {
            console.error('Erro ao criar turma', err);
            alert(err.response?.data?.msg || 'Erro ao criar turma. Tente novamente.');
        } finally {
            setSubmittingTurma(false);
        }
    };

    const handleConcludeTurma = async (turmaId) => {
        if (!window.confirm('Tem certeza que deseja marcar esta turma como Concluída?')) return;
        try {
            await api.put(`/trainings/${turmaId}/conclude`);
            fetchTurmas();
        } catch (err) {
            console.error('Erro ao concluir turma', err);
            alert('Erro ao concluir turma.');
        }
    };

    const handleDeleteTurma = async (turmaId) => {
        if (!window.confirm('Tem certeza que deseja excluir esta turma?')) return;
        try {
            await api.delete(`/trainings/${turmaId}`);
            fetchTurmas();
        } catch (err) {
            console.error('Erro ao excluir turma', err);
            alert('Erro ao excluir turma.');
        }
    };

    const handleShowQrModal = (turma) => {
        setSelectedTurma(turma);
        setShowQrModal(true);
    };

    const attendanceUrl = selectedTurma?.qr_code_token 
        ? `${window.location.origin}/attendance/${selectedTurma.qr_code_token}` 
        : '';

    return (
        <Container className="mt-4">
            <div className="d-flex align-items-center mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/training-list" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Lista de treinamento
                </Button>
            </div>
            
            <Card className="shadow-sm border-0 mb-4">
                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start mb-4 pb-3 border-bottom">
                        <div>
                            <h2 className="mb-4 fw-bold" style={{ color: '#006064' }}>{training.titulo}</h2>
                            <Badge bg={training.status === 'Concluído' ? 'success' : 'warning'} className="fw-normal px-3 py-2 rounded-pill">
                                {training.status}
                            </Badge>
                        </div>
                        {isManagerOrAdmin && (
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{tooltipText}</Tooltip>}
                            >
                                <span className="d-inline-block">
                                    <Button 
                                        variant="primary" 
                                        onClick={() => navigate(`/training/${id}/edit`)}
                                        disabled={isEditDisabled}
                                        className="d-flex align-items-center"
                                        style={isEditDisabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                                    >
                                        <FiEdit className="me-2" /> Editar
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        )}
                    </div>

                    <Row className="mb-4">
                        <Col md={12}>
                            <h5 className="fw-bold mb-3" style={{ color: '#006064' }}>Descrição</h5>
                            <p className="text-muted">{training.descricao || "Sem descrição disponível."}</p>
                        </Col>
                    </Row>

                    {isManagerOrAdmin && (
                        <Row className="mb-4">
                            <Col md={12}>
                                <div className="p-3 bg-light rounded">
                                    <Form.Check
                                        type="switch"
                                        id="double-check-switch"
                                        label="Double-check"
                                        checked={doubleCheckValue}
                                        disabled={isEditDisabled || savingDoubleCheck}
                                        onChange={(e) => handleDoubleCheckChange(e.target.checked)}
                                        className="fw-bold text-secondary"
                                    />
                                    <div className="text-muted small">
                                        {doubleCheckValue ? 'CPF obrigatório no check-in/check-out.' : 'CPF opcional no check-in/check-out.'}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                    
                    <Row className="g-4">
                        <Col md={6}>
                            <div className="d-flex align-items-center p-3 bg-light rounded">
                                <div className="bg-white p-2 rounded-circle shadow-sm me-3 text-primary">
                                    <FiCalendar size={24} />
                                </div>
                                <div>
                                    <h6 className="mb-1 fw-bold text-dark">Data e Hora</h6>
                                    <p className="mb-0 text-muted">{new Date(training.data).toLocaleString()}</p>
                                </div>
                            </div>
                        </Col>
                        
                        <Col md={6}>
                            <div className="d-flex align-items-center p-3 bg-light rounded">
                                <div className="bg-white p-2 rounded-circle shadow-sm me-3 text-primary">
                                    <FiMapPin size={24} />
                                </div>
                                <div>
                                    <h6 className="mb-1 fw-bold text-dark">Local</h6>
                                    <p className="mb-0 text-muted">{training.local || "Não informado"}</p>
                                </div>
                            </div>
                        </Col>
                        
                        <Col md={6}>
                            <div className="d-flex align-items-center p-3 bg-light rounded">
                                <div className="bg-white p-2 rounded-circle shadow-sm me-3 text-primary">
                                    <FiClock size={24} />
                                </div>
                                <div>
                                    <h6 className="mb-1 fw-bold text-dark">Duração</h6>
                                    <p className="mb-0 text-muted">{training.duracao || "Não informada"}</p>
                                </div>
                            </div>
                        </Col>
                        
                        <Col md={6}>
                            <div className="d-flex align-items-center p-3 bg-light rounded">
                                <div className="bg-white p-2 rounded-circle shadow-sm me-3 text-primary">
                                    <FiGlobe size={24} />
                                </div>
                                <div>
                                    <h6 className="mb-1 fw-bold text-dark">Link Online</h6>
                                    {training.link_online ? (
                                        <a href={training.link_online} target="_blank" rel="noopener noreferrer" className="fw-bold text-decoration-none">
                                            Acessar Link
                                        </a>
                                    ) : (
                                        <p className="mb-0 text-muted">Não disponível</p>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Turmas / Classes Section */}
            <Card className="shadow-sm border-0 mb-5">
                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 fw-bold" style={{ color: '#006064' }}>Turmas Relacionadas</h4>
                    {isManagerOrAdmin && !isConcluded && (
                        <Button 
                            variant="primary" 
                            size="sm" 
                            className="d-flex align-items-center gap-1"
                            onClick={() => setShowTurmaModal(true)}
                        >
                            <FiPlus /> Nova Turma
                        </Button>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {loadingTurmas ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="md" />
                        </div>
                    ) : turmas.length > 0 ? (
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Identificação</th>
                                    <th>Data e Hora</th>
                                    <th>Local/Sala</th>
                                    <th>Status</th>
                                    <th className="text-end pe-4">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {turmas.map(t => (
                                    <tr key={t.id}>
                                        <td className="ps-4 fw-bold text-dark">{t.titulo}</td>
                                        <td className="text-muted">{new Date(t.data).toLocaleString()}</td>
                                        <td className="text-muted">{t.sala_nome ? `Sala: ${t.sala_nome}` : (t.local || '-')}</td>
                                        <td>
                                            <Badge bg={t.status === 'Concluído' ? 'success' : 'warning'} className="fw-normal rounded-pill">
                                                {t.status}
                                            </Badge>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="outline-info"
                                                    onClick={() => handleShowQrModal(t)}
                                                    title="Visualizar QR Code de Check-in"
                                                >
                                                    QR Code
                                                </Button>
                                                {isManagerOrAdmin && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-success"
                                                            disabled={t.status === 'Concluído'}
                                                            onClick={() => handleConcludeTurma(t.id)}
                                                            title="Concluir Turma"
                                                        >
                                                            <FiCheckCircle />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() => handleDeleteTurma(t.id)}
                                                            title="Excluir Turma"
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            Nenhuma turma registrada para este treinamento.
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal para Nova Turma */}
            <Modal show={showTurmaModal} onHide={() => setShowTurmaModal(false)} size="lg" centered className="training-modal-compact">
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold" style={{ color: '#006064' }}>Agendar Nova Turma</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateTurmaSubmit}>
                        <Row className="g-3">
                            <Col md={12}>
                                <FloatingLabel controlId="turmaTitulo" label="Identificação da Turma *">
                                    <Form.Control
                                        type="text"
                                        placeholder="Ex: Turma A, Turno Manhã"
                                        name="titulo"
                                        value={turmaFormData.titulo}
                                        onChange={handleTurmaFormChange}
                                        required
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaData" label="Data *">
                                    <Form.Control
                                        type="date"
                                        name="data"
                                        value={turmaFormData.data}
                                        onChange={handleTurmaFormChange}
                                        required
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaHora" label="Hora de Início *">
                                    <Form.Control
                                        type="time"
                                        name="time"
                                        value={turmaFormData.time}
                                        onChange={handleTurmaFormChange}
                                        required
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaLocal" label="Local Físico (Opcional)">
                                    <Form.Control
                                        type="text"
                                        placeholder="Ex: Auditório Central"
                                        name="local"
                                        value={turmaFormData.local}
                                        onChange={handleTurmaFormChange}
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaSala" label="Sala do Sistema (Opcional)">
                                    <Form.Select
                                        name="sala_id"
                                        value={turmaFormData.sala_id}
                                        onChange={handleTurmaFormChange}
                                    >
                                        <option value="">Nenhuma sala cadastrada</option>
                                        {rooms.map(r => (
                                            <option key={r.id} value={r.id}>{r.nome} (Capacidade: {r.capacidade})</option>
                                        ))}
                                    </Form.Select>
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaDuracao" label="Duração em Horas *">
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        placeholder="Ex: 2"
                                        name="duracao"
                                        value={turmaFormData.duracao}
                                        onChange={handleTurmaFormChange}
                                        required
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaLink" label="Link de Transmissão (Online)">
                                    <Form.Control
                                        type="url"
                                        placeholder="https://..."
                                        name="link_online"
                                        value={turmaFormData.link_online}
                                        onChange={handleTurmaFormChange}
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaTolIn" label="Tolerância de Check-in (Minutos)">
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        name="tolerance_checkin"
                                        value={turmaFormData.tolerance_checkin}
                                        onChange={handleTurmaFormChange}
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6}>
                                <FloatingLabel controlId="turmaTolOut" label="Tolerância de Check-out (Minutos)">
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        name="tolerance_checkout"
                                        value={turmaFormData.tolerance_checkout}
                                        onChange={handleTurmaFormChange}
                                    />
                                </FloatingLabel>
                            </Col>

                            <Col md={6} className="d-flex align-items-center">
                                <Form.Check
                                    type="switch"
                                    id="turmaObrigaCheckout"
                                    label="Obriga Checkout"
                                    name="obriga_checkout"
                                    checked={turmaFormData.obriga_checkout}
                                    onChange={handleTurmaFormChange}
                                    className="fw-bold text-secondary"
                                />
                            </Col>

                            <Col md={6} className="d-flex align-items-center">
                                <Form.Check
                                    type="switch"
                                    id="turmaDoubleCheck"
                                    label="CPF Obrigatório no Check-in"
                                    name="double_check"
                                    checked={turmaFormData.double_check}
                                    onChange={handleTurmaFormChange}
                                    className="fw-bold text-secondary"
                                />
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                            <Button variant="outline-secondary" onClick={() => setShowTurmaModal(false)}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submittingTurma}>
                                {submittingTurma ? 'Salvando...' : 'Criar Turma'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Modal para QR Code */}
            <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered className="feedback-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold" style={{ color: '#006064' }}>QR Code de Presença</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-4">
                    {selectedTurma && (
                        <>
                            <h5 className="fw-bold mb-3">{selectedTurma.titulo}</h5>
                            <div className="p-3 bg-white d-inline-block rounded shadow-sm mb-3" style={{ border: '1px solid #eee' }}>
                                <QRCodeCanvas value={attendanceUrl} size={256} />
                            </div>
                            <p className="text-muted small mb-0">
                                Aponte a câmera do dispositivo móvel para registrar a presença nesta turma.
                            </p>
                            <div className="mt-3 p-2 bg-light rounded text-break font-monospace small" style={{ fontSize: '0.8rem', color: '#006064' }}>
                                {attendanceUrl}
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default TrainingDetail;
